import type {
  Customer,
  PortfolioItem,
  ProductMarket,
  LegacyProduct,
  LegacyChildProduct,
} from '@app/types';
import { AnalyticsAttributes, GTMAttributes } from '../../consts';
import { TrackingProperties, TrackPayloadDecorators } from '../types';

/**
 * Product -> Tracking Props
 */
const productToProperties = (
  product: LegacyProduct | LegacyChildProduct,
  tracker: string,
): TrackingProperties => {
  if (product == null) {
    return {};
  }

  let skuUuid;

  switch (tracker) {
    case 'ga':
      if ('variantId' in product) {
        skuUuid = product.variantId;
      } else if ('parentId' in product) {
        skuUuid = product.id;
      } else {
        skuUuid = undefined;
      }

      return {
        [AnalyticsAttributes.cdProductVertical]: product.productCategory,
        [AnalyticsAttributes.cdProductId]:
          'parentId' in product ? product.parentId : product.id,
        [AnalyticsAttributes.cdProductName]: product.title,
        [AnalyticsAttributes.cdProductPrimaryCategory]: product.primaryCategory,
        [AnalyticsAttributes.cdProductBrand]: product.brand,
        [AnalyticsAttributes.cdSkuUuid]: skuUuid,
        [AnalyticsAttributes.cdHighestBidLocalAmount]:
          product.market?.bidAskData?.highestBid || product.market?.highestBid,
        [AnalyticsAttributes.cdLowestAskLocalAmount]: product.market?.lowestAsk,
        [AnalyticsAttributes.cdPromotionalProduct]:
          product.meta?.charity ||
          product.meta?.raffle ||
          product.meta?.restock ||
          product.listingType === 'charity' ||
          product.listingType === 'raffle' ||
          product.listingType === 'restock',
      };
    case 'gtm':
      return {
        [GTMAttributes.productId]:
          'parentId' in product ? product.parentId : product.id,
        [GTMAttributes.productSkuUuid]: product.id,
        [GTMAttributes.productBrand]: product.brand,
        [GTMAttributes.productCategory]: product.contentGroup,
        [GTMAttributes.productVertical]: product.productCategory,
        [GTMAttributes.productVariant]: product.colorway,
        [GTMAttributes.productVariantSize]: product.shoeSize ?? 'GENERIC',
        [GTMAttributes.productMarket]: product.market,
      };
    default:
      return {};
  }
};

/**
 * Market -> Tracking Props
 */
const marketDataToProperties = (
  market: ProductMarket,
  tracker: string,
): TrackingProperties => {
  if (market == null) {
    return {};
  }

  switch (tracker) {
    case 'ga':
      return {
        [AnalyticsAttributes.cdHighestBidLocalAmount]: market?.highestBid,
        [AnalyticsAttributes.cdLowestAskLocalAmount]: market?.lowestAsk,
        [AnalyticsAttributes.cdLocalCurrency]: market?.currencyCode,
      };
    default:
      return {};
  }
};

/**
 * Portfolio Item -> Tracking Props
 */
export const portfolioItemToProperties = (
  portfolioItem: PortfolioItem,
  tracker: string,
) => {
  switch (tracker) {
    case 'ga':
      return {
        [AnalyticsAttributes.cdCustomerLoggedInUUID]: portfolioItem.userUuid,
        [AnalyticsAttributes.cdChainId]: portfolioItem.chainId ?? '',
        [AnalyticsAttributes.cdLocalAmount]: portfolioItem.localAmount,
        [AnalyticsAttributes.cdLocalCurrency]: portfolioItem.localCurrency,
        [AnalyticsAttributes.cdCouponCode]:
          portfolioItem.meta?.discountCode ?? '',
      };
    default:
      return {};
  }
};

/**
 * Customer -> Tracking Props
 */
const customerToProperties = (customer: Customer, tracker: string) => {
  switch (tracker) {
    case 'ga':
      return {
        [AnalyticsAttributes.cdCustomerLoggedInUUID]: customer.uuid,
      };
    case 'gtm':
      return {
        [GTMAttributes.customerId]: customer.uuid,
      };
    default:
      return {};
  }
};

/**
 *
 */
const decorateAnalytics = (
  decorators: TrackPayloadDecorators,
  tracker: string,
): TrackingProperties => ({
  ...(decorators.product
    ? productToProperties(decorators.product, tracker)
    : {}),
  ...(decorators.market
    ? marketDataToProperties(decorators.market, tracker)
    : {}),
  ...(decorators.portfolioItem
    ? portfolioItemToProperties(decorators.portfolioItem, tracker)
    : {}),
  ...(decorators.customer
    ? customerToProperties(decorators.customer, tracker)
    : {}),
});

export default decorateAnalytics;
